import { Link } from 'react-router-dom';
import { Cookie, Settings, BarChart3, Target } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { SEO } from '../components/SEO';

export const Cookies = () => {
  const lastUpdated = "December 2025";

  const handleOpenCookieSettings = () => {
    // Trigger cookie settings modal
    localStorage.removeItem('cookie_consent');
    window.location.reload();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO 
        title="Cookie Policy - Crescent Electrical Ltd"
        description="Cookie Policy for Crescent Electrical Ltd. Information about cookies and tracking technologies we use."
        canonical="https://crescentelectrical.co.uk/cookies"
      />

      {/* Hero */}
      <section className="bg-gradient-to-br from-[#2563EB] to-blue-700 text-white py-16">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-4">
            <Cookie className="h-10 w-10" />
            <h1 className="text-4xl sm:text-5xl font-bold">Cookie Policy</h1>
          </div>
          <p className="text-xl text-blue-100">
            How Crescent Electrical Ltd uses cookies and similar technologies
          </p>
          <p className="text-sm text-blue-200 mt-4">Last updated: {lastUpdated}</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          
          {/* Cookie Settings CTA */}
          <Card className="bg-[#2563EB] text-white border-0 mb-8">
            <CardContent className="pt-6 flex flex-col md:flex-row items-center justify-between gap-4">
              <div>
                <h3 className="text-xl font-bold mb-2">Manage Your Cookie Preferences</h3>
                <p className="text-blue-100">You can change your cookie settings at any time</p>
              </div>
              <Button 
                onClick={handleOpenCookieSettings}
                variant="secondary"
                size="lg"
                className="bg-white text-[#2563EB] hover:bg-blue-50 gap-2"
              >
                <Settings className="h-5 w-5" />
                Cookie Settings
              </Button>
            </CardContent>
          </Card>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 mb-8">
            <div className="prose prose-blue max-w-none">
              
              {/* Introduction */}
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">1. What Are Cookies?</h2>
                <p className="text-gray-700 mb-3">
                  Cookies are small text files placed on your device (computer, tablet, phone) when you visit a website. They help websites remember your preferences and understand how you use the site.
                </p>
                <p className="text-gray-700">
                  Similar technologies include local storage, session storage, and pixels. This policy covers all such technologies (collectively referred to as "cookies").
                </p>
              </div>

              {/* Why We Use Cookies */}
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Why We Use Cookies</h2>
                <p className="text-gray-700 mb-3">
                  We use cookies to:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>Ensure the website functions correctly (necessary cookies)</li>
                  <li>Remember your cookie preferences</li>
                  <li>Understand how visitors use our website (analytics)</li>
                  <li>Measure the effectiveness of our advertising (marketing)</li>
                  <li>Improve user experience</li>
                </ul>
              </div>

              {/* Types of Cookies */}
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Types of Cookies We Use</h2>
                
                <div className="space-y-6">
                  {/* Necessary Cookies */}
                  <Card className="border-gray-200">
                    <CardContent className="pt-6">
                      <div className="flex items-start gap-3 mb-3">
                        <Settings className="h-6 w-6 text-gray-900 flex-shrink-0 mt-1" />
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 mb-2">Necessary Cookies (Always Active)</h3>
                          <p className="text-sm text-gray-700 mb-3">
                            These cookies are essential for the website to function and cannot be switched off. They are usually set in response to actions you take, such as submitting forms or setting privacy preferences.
                          </p>
                        </div>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-2 text-left font-semibold text-gray-900">Cookie Name</th>
                              <th className="px-4 py-2 text-left font-semibold text-gray-900">Purpose</th>
                              <th className="px-4 py-2 text-left font-semibold text-gray-900">Duration</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            <tr>
                              <td className="px-4 py-2 text-gray-700">cookie_consent</td>
                              <td className="px-4 py-2 text-gray-700">Stores your cookie preferences</td>
                              <td className="px-4 py-2 text-gray-700">1 year</td>
                            </tr>
                            <tr>
                              <td className="px-4 py-2 text-gray-700">session_token</td>
                              <td className="px-4 py-2 text-gray-700">Admin authentication (if logged in)</td>
                              <td className="px-4 py-2 text-gray-700">Session</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Analytics Cookies */}
                  <Card className="border-blue-200 bg-blue-50">
                    <CardContent className="pt-6">
                      <div className="flex items-start gap-3 mb-3">
                        <BarChart3 className="h-6 w-6 text-[#2563EB] flex-shrink-0 mt-1" />
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 mb-2">Analytics Cookies (Requires Consent)</h3>
                          <p className="text-sm text-gray-700 mb-3">
                            These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously. We use Google Analytics 4.
                          </p>
                        </div>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="min-w-full text-sm bg-white rounded">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-2 text-left font-semibold text-gray-900">Cookie Name</th>
                              <th className="px-4 py-2 text-left font-semibold text-gray-900">Provider</th>
                              <th className="px-4 py-2 text-left font-semibold text-gray-900">Purpose</th>
                              <th className="px-4 py-2 text-left font-semibold text-gray-900">Duration</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            <tr>
                              <td className="px-4 py-2 text-gray-700">_ga</td>
                              <td className="px-4 py-2 text-gray-700">Google Analytics</td>
                              <td className="px-4 py-2 text-gray-700">Distinguishes unique users</td>
                              <td className="px-4 py-2 text-gray-700">2 years</td>
                            </tr>
                            <tr>
                              <td className="px-4 py-2 text-gray-700">_ga_*</td>
                              <td className="px-4 py-2 text-gray-700">Google Analytics</td>
                              <td className="px-4 py-2 text-gray-700">Maintains session state</td>
                              <td className="px-4 py-2 text-gray-700">2 years</td>
                            </tr>
                            <tr>
                              <td className="px-4 py-2 text-gray-700">tracking_params</td>
                              <td className="px-4 py-2 text-gray-700">Crescent Electrical</td>
                              <td className="px-4 py-2 text-gray-700">Stores UTM parameters for conversion tracking</td>
                              <td className="px-4 py-2 text-gray-700">Session</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                      <p className="text-xs text-gray-600 mt-3">
                        Learn more: <a href="https://policies.google.com/technologies/cookies" target="_blank" rel="noopener noreferrer" className="text-[#2563EB] hover:underline">Google's Cookie Policy</a>
                      </p>
                    </CardContent>
                  </Card>

                  {/* Advertising/Marketing Cookies */}
                  <Card className="border-orange-200 bg-orange-50">
                    <CardContent className="pt-6">
                      <div className="flex items-start gap-3 mb-3">
                        <Target className="h-6 w-6 text-[#F97316] flex-shrink-0 mt-1" />
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 mb-2">Advertising/Marketing Cookies (Requires Consent)</h3>
                          <p className="text-sm text-gray-700 mb-3">
                            These cookies track your online activity to help advertisers deliver more relevant advertising and measure campaign performance. We use Google Ads conversion tracking.
                          </p>
                        </div>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="min-w-full text-sm bg-white rounded">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-2 text-left font-semibold text-gray-900">Cookie Name</th>
                              <th className="px-4 py-2 text-left font-semibold text-gray-900">Provider</th>
                              <th className="px-4 py-2 text-left font-semibold text-gray-900">Purpose</th>
                              <th className="px-4 py-2 text-left font-semibold text-gray-900">Duration</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            <tr>
                              <td className="px-4 py-2 text-gray-700">_gcl_*</td>
                              <td className="px-4 py-2 text-gray-700">Google Ads</td>
                              <td className="px-4 py-2 text-gray-700">Conversion tracking and attribution</td>
                              <td className="px-4 py-2 text-gray-700">90 days</td>
                            </tr>
                            <tr>
                              <td className="px-4 py-2 text-gray-700">gclid</td>
                              <td className="px-4 py-2 text-gray-700">Google Ads</td>
                              <td className="px-4 py-2 text-gray-700">Click identifier for ad campaigns</td>
                              <td className="px-4 py-2 text-gray-700">Session</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                      <p className="text-xs text-gray-600 mt-3">
                        Learn more: <a href="https://policies.google.com/technologies/ads" target="_blank" rel="noopener noreferrer" className="text-[#2563EB] hover:underline">Google Ads Privacy</a>
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </div>

              {/* Google Consent Mode */}
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Google Consent Mode v2</h2>
                <p className="text-gray-700 mb-3">
                  We implement Google Consent Mode v2 to respect your privacy choices. This technology adjusts how Google tags behave based on your consent:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li><strong>analytics_storage:</strong> Controls analytics cookies (Google Analytics)</li>
                  <li><strong>ad_storage:</strong> Controls advertising cookies (Google Ads)</li>
                  <li><strong>ad_user_data:</strong> Controls user data sharing for advertising</li>
                  <li><strong>ad_personalization:</strong> Controls personalized advertising</li>
                </ul>
                <p className="text-gray-700 mt-3">
                  If you decline analytics or advertising cookies, Google's tags operate in a limited mode that does not use cookies for those purposes.
                </p>
              </div>

              {/* Managing Cookies */}
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">5. How to Control Cookies</h2>
                <p className="text-gray-700 mb-4">
                  You have several options to manage cookies:
                </p>
                
                <h3 className="text-lg font-semibold text-gray-900 mb-3">On This Website:</h3>
                <p className="text-gray-700 mb-3">
                  Use our Cookie Settings to accept or reject analytics and advertising cookies at any time:
                </p>
                <Button 
                  onClick={handleOpenCookieSettings}
                  className="bg-[#2563EB] hover:bg-blue-700 gap-2 mb-4"
                >
                  <Settings className="h-5 w-5" />
                  Open Cookie Settings
                </Button>

                <h3 className="text-lg font-semibold text-gray-900 mb-3 mt-6">In Your Browser:</h3>
                <p className="text-gray-700 mb-3">
                  Most browsers allow you to refuse or delete cookies. The method varies by browser and version. Here are links to common browsers:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="text-[#2563EB] hover:underline">Google Chrome</a></li>
                  <li><a href="https://support.mozilla.org/en-US/kb/enhanced-tracking-protection-firefox-desktop" target="_blank" rel="noopener noreferrer" className="text-[#2563EB] hover:underline">Mozilla Firefox</a></li>
                  <li><a href="https://support.apple.com/en-gb/guide/safari/sfri11471/mac" target="_blank" rel="noopener noreferrer" className="text-[#2563EB] hover:underline">Apple Safari</a></li>
                  <li><a href="https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer" className="text-[#2563EB] hover:underline">Microsoft Edge</a></li>
                </ul>
                <p className="text-gray-700 mt-3 text-sm italic">
                  Please note: Blocking necessary cookies may prevent the website from functioning correctly.
                </p>
              </div>

              {/* Third-Party Opt-Out */}
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Third-Party Advertising Opt-Out</h2>
                <p className="text-gray-700 mb-3">
                  You can opt out of personalized advertising from Google:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li><a href="https://adssettings.google.com/" target="_blank" rel="noopener noreferrer" className="text-[#2563EB] hover:underline">Google Ads Settings</a></li>
                  <li><a href="https://www.youronlinechoices.com/uk/" target="_blank" rel="noopener noreferrer" className="text-[#2563EB] hover:underline">Your Online Choices (UK)</a></li>
                </ul>
              </div>

              {/* Contact */}
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Questions About Cookies?</h2>
                <p className="text-gray-700 mb-3">
                  If you have questions about our use of cookies, please contact us:
                </p>
                <p className="text-gray-700">
                  Email: <a href="mailto:info@electriccertificate.co.uk" className="text-[#2563EB] hover:underline">info@electriccertificate.co.uk</a><br />
                  Phone: <a href="tel:07711233685" className="text-[#2563EB] hover:underline">07711 233685</a>
                </p>
              </div>

            </div>
          </div>

          {/* Quick Links */}
          <div className="grid md:grid-cols-2 gap-6">
            <Link to="/privacy">
              <Card className="border-gray-200 hover:border-[#2563EB] hover:shadow-lg transition-all h-full">
                <CardContent className="pt-6">
                  <h3 className="font-semibold text-gray-900 mb-2">Privacy Policy</h3>
                  <p className="text-sm text-gray-600">How we handle your personal information</p>
                </CardContent>
              </Card>
            </Link>
            <Link to="/terms">
              <Card className="border-gray-200 hover:border-[#2563EB] hover:shadow-lg transition-all h-full">
                <CardContent className="pt-6">
                  <h3 className="font-semibold text-gray-900 mb-2">Website Terms</h3>
                  <p className="text-sm text-gray-600">Terms and conditions of use</p>
                </CardContent>
              </Card>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
